import { FormControl, MenuItem } from '@mui/material';
import { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { alphaSort } from '../../helpers';
import { GetSalesLookupNeighborhoodsParams, IDropdownResponse } from '../../models';
import { SMBSelect } from '../input';
import { getSalesLookupNeighborhoods } from '../../fetch/lookups/sales-portal';

interface ISalesNeighborhoodDropdownProps {
  value: string | number;
  handleChange: (val: string | number) => void;
  isDisabled?: boolean;
  setOptions?: (val: IDropdownResponse[]) => void;
  apiParams?: GetSalesLookupNeighborhoodsParams;
  isRequired?: boolean;
  inputLabel?: string;
  returnNeighborhoodName?: boolean;
  isEnabled?: boolean;
  name?: string;
  handleClear?: () => void;
}

export const SalesNeighborhoodDropdown: FC<ISalesNeighborhoodDropdownProps> = ({
  inputLabel,
  isDisabled,
  isRequired,
  value,
  handleChange,
  setOptions,
  apiParams,
  returnNeighborhoodName,
  name,
  isEnabled = true,
  handleClear
}) => {
  const { isLoading: isLoadingNeighborhoods, data: neighborhoodsData } = useQuery<
    IDropdownResponse[],
    Error
  >(['neighborhoods-filters', apiParams], () => getSalesLookupNeighborhoods(apiParams), {
    notifyOnChangeProps: 'tracked',
    onSuccess: d => setOptions?.(d),
    enabled: isEnabled
  });

  const neighborhoods = useMemo(() => {
    // Ensure neighborhoodsData is an array
    if (!Array.isArray(neighborhoodsData)) {
      // add error logging
      console.error('Expected an array for neighborhoodsData, received:', typeof neighborhoodsData);
      // Return an empty array until the data arrives in the expected array format
      return [];
    }
    return neighborhoodsData.map(r => ({
      ...r,
      fullName: `${r.description}`
    }));
  }, [neighborhoodsData]);

  return (
    <FormControl fullWidth variant="outlined" size="small" required={isRequired}>
      <SMBSelect
        name={name || null}
        disabled={isDisabled || isLoadingNeighborhoods}
        label={isLoadingNeighborhoods ? 'Loading...' : inputLabel ?? 'Neighborhood'}
        value={value}
        required={isRequired}
        onChange={({ target: { value } }) => {
          handleChange(value);
        }}
        handleClear={() => {
          handleChange('');
          handleClear && handleClear();
        }}
        SelectProps={{
          MenuProps: {
            sx: {
              maxHeight: '30rem'
            }
          }
        }}
      >
        {!isLoadingNeighborhoods && neighborhoods && neighborhoods.length === 0 && (
          <MenuItem disabled>No Neighborhoods available</MenuItem>
        )}
        {!isLoadingNeighborhoods &&
          neighborhoods &&
          alphaSort(neighborhoods, 'description').map((neighborhood, index) => {
            return (
              <MenuItem key={`${index}`} value={neighborhood.value}>
                {neighborhood.description}
              </MenuItem>
            );
          })}
      </SMBSelect>
    </FormControl>
  );
};
